import type { GetCellPhoneWithFamilyPlanCostByProductLinkUrlRequest } from '~/models/cellphone_page/WithFamilyPlanCostRequest.model';
import type { ListingProduct } from '~/models/ListingProduct.api.model';

const _stopLoadingWithErrorMsg = (msg?: string) => {
  const familyStore = useFamilyStore();

  familyStore.ui.generalErrorMessage = msg || 'Kunde inte hämta abonnemang';
  familyStore.ui.isBuyButtonLoading = false;
};

const _determineIfNewCustomer = (): boolean => {
  const familyStore = useFamilyStore();

  return (
    !familyStore.existingCustomer.isIdentified &&
    !familyStore.existingCustomer.hasPlans
  );
};

const _selectBindingPeriodId = (): number | undefined => {
  const familyStore = useFamilyStore();

  const bindingPeriod = familyStore.selectedBindingPeriod;
  const bindingPeriodId = familyStore.selectedFamilyPlan?.bundlePrices.find(
    findBundlePriceByBindingPeriod(bindingPeriod),
  )?.BindingPeriod.BindingPeriodId;

  return bindingPeriodId;
};

const _selectParentPlanIds = (
  bindingPeriodId: number,
): (number | undefined)[] => {
  const familyStore = useFamilyStore();
  const productPageStore = useMyProductPageStore();

  const parentPlanId = familyStore.selectedParentPlan?.planId;

  const parentPlanBundlePriceId =
    productPageStore.currentVariant?.Prices?.BundlePrices?.find(
      (bundlePrice) =>
        bundlePrice.Plan?.PlanId === familyStore.selectedParentPlan?.planId &&
        bundlePrice.BindingPeriod.BindingPeriodId === bindingPeriodId &&
        bundlePrice.Plan?.IsFamilyAllowed === true,
    )?.BundlePriceId;

  return [parentPlanId, parentPlanBundlePriceId];
};

const _selectFamilyPlanIdsFromResponse = (
  response: ListingProduct,
): (number | undefined)[] => {
  const familyPlanBundlePriceId = response.FamilyPlansSummary?.find(
    (bundlePrice) =>
      bundlePrice.Plan?.IsFamilyPlan === true &&
      bundlePrice.Plan?.IsFamilyAllowed === false,
  )?.BundlePriceId;

  const familyPlanPlanId = response.FamilyPlansSummary?.find(
    (bundlePrice) =>
      bundlePrice.Plan?.IsFamilyPlan === true &&
      bundlePrice.Plan?.IsFamilyAllowed === false,
  )?.Plan?.PlanId;

  return [familyPlanBundlePriceId, familyPlanPlanId];
};

const _closeDrawerAndShowFamilyTable = () => {
  const familyStore = useFamilyStore();

  familyStore.ui.showFamilyAddedPdp = true;
  familyStore.ui.showFamilyDrawer = false;
};

const _selectExistingCustomerPhoneNumberIfExists = (
  isNewCustomer: boolean,
): string | undefined => {
  const familyStore = useFamilyStore();

  if (
    isNewCustomer ||
    familyStore.existingCustomer.plans.length <= 0 ||
    !familyStore.selectedExistingCustomerPlanNumber
  ) {
    return undefined;
  }

  return familyStore.selectedExistingCustomerPlanNumber;
};

const _showLoading = () => {
  const familyStore = useFamilyStore();
  const productPageStore = useMyProductPageStore();

  familyStore.ui.isBuyButtonLoading = true;
  productPageStore.ui.loading.isFamilySummaryTableLoading = true;
};

const _hideLoading = () => {
  const familyStore = useFamilyStore();
  const productPageStore = useMyProductPageStore();

  familyStore.ui.isBuyButtonLoading = false;
  productPageStore.ui.loading.isFamilySummaryTableLoading = false;
};

const _areArraysEqual = (a?: any[] | null, b?: any[] | null): boolean => {
  // Check both null and undefined with '=='
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  return a.length === b.length && a.every((value, index) => value === b[index]);
};

export const family_addFamilyPlanDataToPdp = async () => {
  const familyStore = useFamilyStore();
  const productPageStore = useMyProductPageStore();
  const { $api } = useNuxtApp();

  _showLoading();

  const isNewCustomer = _determineIfNewCustomer();
  const bindingPeriodId = _selectBindingPeriodId();

  if (!bindingPeriodId) {
    _stopLoadingWithErrorMsg(
      'Kunde inte hämta rätt familjeabonnemang att visa.',
    );
    return;
  }

  const [parentPlanId, parentPlanBundlePriceId] =
    _selectParentPlanIds(bindingPeriodId);

  const bundlePriceIdOfCorrespondingProductVariantFamilyPlan =
    productPageStore.currentVariant?.Prices?.BundlePrices?.find(
      (bundlePrice) =>
        bundlePrice.Plan?.PlanId === familyStore.selectedFamilyPlan?.planId &&
        _areArraysEqual(
          bundlePrice.Plan?.ParentPlanIds,
          familyStore.selectedFamilyPlan?.parentPlanIds,
        ) &&
        bundlePrice.BindingPeriod.BindingPeriodId === bindingPeriodId &&
        bundlePrice.Plan?.IsFamilyPlan === true,
    )?.BundlePriceId;

  if (parentPlanId === undefined || parentPlanId === null) {
    _stopLoadingWithErrorMsg('Kunde inte hämta rätt huvudabonnemang att visa.');
    return;
  }

  if (!parentPlanBundlePriceId) {
    _stopLoadingWithErrorMsg(
      'Kunde inte hämta rätt familjeabonnemang att visa.',
    );
    return;
  }

  if (!bundlePriceIdOfCorrespondingProductVariantFamilyPlan) {
    _stopLoadingWithErrorMsg(
      'Kunde inte hämta rätt familjeabonnemang att visa.',
    );
    return;
  }

  const existingCustomerPlanPhoneNumber =
    _selectExistingCustomerPhoneNumberIfExists(isNewCustomer);

  const params: GetCellPhoneWithFamilyPlanCostByProductLinkUrlRequest = {
    productLinkUrl: productPageStore.product?.ProductLinkUrl ?? '',
    mainBundlePriceId: parentPlanBundlePriceId,
    mainMsisdnOptionId: isNewCustomer ? 1 : 2,
    familyBundlePriceId: bundlePriceIdOfCorrespondingProductVariantFamilyPlan,
    noOfFamilyPlans: familyStore.numberOfFamilyPlanConnections,
    bindingPeriodId: bindingPeriodId,
  };

  try {
    const response =
      await $api.cellPhonePage.getCellPhoneWithFamilyPlanCostByProductLinkUrlAsync(
        params,
      );

    const [familyPlanBundlePriceId, familyPlanPlanId] =
      _selectFamilyPlanIdsFromResponse(response);

    if (!familyPlanBundlePriceId || !familyPlanPlanId) {
      _stopLoadingWithErrorMsg('Kunde inte hämta rätt familjeanslutning.');
      return;
    }

    productPageStore.setFamilyPlan(
      {
        familyPlanBundlePriceId: familyPlanBundlePriceId,
        familyPlanParentPhoneNumber: existingCustomerPlanPhoneNumber,
        familyPlanPlanId: familyPlanPlanId,
        familyPlanSavings: response.FamilyPlanSavings,
        familyPlansTotalRegularMonthlyFee:
          response.FamilyPlansTotalRegularMonthlyFee,
        familyPlansTotalMonthlyFee: response.FamilyPlansTotalMonthlyFee,
        familyPlansTotalIntermediateMonthlyFee:
          response.FamilyPlansTotalIntermediateMonthlyFee,
        familyPlanSummaryItems: response.FamilyPlansSummary,
        isNewCustomer: isNewCustomer,
        numberOfConnections:
          response.FamilyPlansSummary?.filter(
            (bundlePrice) => bundlePrice.Plan?.IsFamilyPlan === true,
          )?.length ?? undefined,
        parentPlanBundlePriceId: parentPlanBundlePriceId,
        totalCostOverBindingPeriodForFamilyPlans:
          response.FamilyPlansTotalCostOverBindingPeriod,
        totalCostOverBindingPeriodIncludingFamilyPlans:
          response.TotalCostOverBindingPeriod,
      },
      parentPlanId,
    );

    _closeDrawerAndShowFamilyTable();
  } catch (e) {
    _stopLoadingWithErrorMsg(
      'Kunde inte lägga till familjeabonnemanget. Kontrollera att du har ett giltigt abonnemang eller kontakta våran support!',
    );
    console.error(e);
  } finally {
    _hideLoading();
  }
};
